import React, { useState, useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const DegreeFilters = ({
  color,
  degrees,
  setDegreeFilter,
  degreesShowing,
  tenantName,
}) => {
  const { t } = useTranslation();

  // magic numbers are for styles
  const maxHeightForCategories = degreesShowing < 3 ? '82px' : '160px';
  const degreeTypes = degrees.map((degree) => degree.degreeTypeEn);
  const locations = degrees
    .map((degree) =>
      degree.campus.split(',').map((location) => location.trim())
    )
    .flat();
  const colleges = degrees
    .map((degree) =>
      degree.institution_info.map((info) => info.institution_name)
    )
    .flat();
  const uniqueDegreeTypes = useMemo(
    () => [...new Set(degreeTypes)],
    [degreeTypes.join()]
  );
  const uniqueLocations = useMemo(
    () => [...new Set(locations)],
    [locations.join()]
  );
  const uniqueColleges = useMemo(
    () => [...new Set(colleges)],
    [colleges.join()]
  );
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [visibleColleges, setvisibleColleges] = useState(uniqueColleges);
  const [visibleDegrees, setVisibleDegrees] = useState(uniqueDegreeTypes);
  const [visibleLocations, setVisibleLocations] = useState(uniqueLocations);

  const divFilters = React.useRef();

  const handleClickOutsideFilter = (event) => {
    if (!(divFilters && divFilters.current)) return;

    if (!divFilters.current.contains(event.target)) {
      setFiltersVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideFilter);

    return () => {
      window.removeEventListener('mousedown', handleClickOutsideFilter);
    };
  }, []);

  useEffect(() => {
    setDegreeFilter({ visibleColleges, visibleDegrees, visibleLocations });
  }, [visibleColleges, visibleDegrees, visibleLocations]);

  useEffect(() => {
    setvisibleColleges(uniqueColleges);
    setVisibleDegrees(uniqueDegreeTypes);
    setVisibleLocations(uniqueLocations);
  }, [uniqueDegreeTypes, uniqueLocations, uniqueColleges]);

  const handleCheck = (item, list, setListInState) => {
    const itemIndex = list.indexOf(item);
    const updatedList = [...list];

    if (itemIndex >= 0) {
      updatedList.splice(itemIndex, 1);
    } else {
      updatedList.push(item);
    }

    setListInState(updatedList);
  };

  const colorPrimary = {
    color: `${color}`,
  };

  const checkBox = (item, list, setListInState, dataCy) => {
    if (!item) return;
    return (
      <div className="application-check-container filter-item" key={item}>
        <div className="checkbox-input" data-cy={dataCy}>
          <input
            type="checkbox"
            value
            tabIndex={0}
            checked={list.includes(item)}
            onChange={() => handleCheck(item, list, setListInState)}
            data-cy="filters-checkbox"
            className="custom-checkbox"
            useHiddenInput
            style={({ border: '1px solid #555555' }, colorPrimary)}
            aria-label={`check to filter by ${item}.`}
          />
          <label data-cy="checkbox-label" className="section-label">
            {item}
          </label>
        </div>
      </div>
    );
  };

  const filtersList = () => (
    <div
      className="filter-design filters-list"
      style={{ color }}
      ref={divFilters}
    >
      <div
        role="button"
        tabIndex={0}
        className="filters"
        onClick={() => setFiltersVisible(!filtersVisible)}
        onKeyDown={(e) => {
          if (['Enter'].includes(e.key)) setFiltersVisible(!filtersVisible);
        }}
        aria-label={t('filter options', { context: 'search' })}
      >
        <p>{t('More Filters')}</p>
        <p>{`- ${t('Close', { context: 'action' })}`}</p>
      </div>
      <hr />
      <div>
        {/* {i don't love this but until we know how this will shape up with other tenants, lets leave it and make it a feature flag when we have more info} */}
        {tenantName === 'Nevada' ? null : (
          <fieldset>
            <legend className="label is-small filter-label">
              {t('Locations')}
            </legend>
            <div
              className="category"
              style={{ maxHeight: maxHeightForCategories }}
            >
              {uniqueLocations.map((location) =>
                checkBox(
                  location,
                  visibleLocations,
                  setVisibleLocations,
                  'locations'
                )
              )}
            </div>
            <hr />
          </fieldset>
        )}
        <fieldset>
          <legend className="label is-small filter-label">
            {t('Colleges/Schools')}
          </legend>
          <div
            className="category"
            style={{ maxHeight: maxHeightForCategories }}
          >
            {uniqueColleges.map((college) =>
              checkBox(
                college,
                visibleColleges,
                setvisibleColleges,
                'colleges',
                'colleges-schools'
              )
            )}
          </div>
        </fieldset>
        <hr />
        {uniqueDegreeTypes.filter((degree) => {
          return !isEmpty(degree) && !degree.type;
        }).length > 0 ? (
          <fieldset>
            <legend className="label is-small filter-label">
              {t('Credential Types')}
            </legend>
            <div
              className="category"
              style={{ maxHeight: maxHeightForCategories }}
            >
              {uniqueDegreeTypes.map((type) =>
                checkBox(
                  type,
                  visibleDegrees,
                  setVisibleDegrees,
                  'degrees',
                  'credential-types'
                )
              )}
            </div>
          </fieldset>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="filters-container">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setFiltersVisible(!filtersVisible)}
        onKeyDown={(e) => {
          if (['Enter'].includes(e.key)) setFiltersVisible(!filtersVisible);
        }}
        className="filter-design"
        style={{ color, visibility: filtersVisible ? 'hidden' : 'visible' }}
        aria-label={t('More Filters', { context: 'search' })}
        data-cy="filters-button"
      >
        <div className="filters">
          <p>{t('More Filters')}</p>
          <p>{`+ ${t('Open', { context: 'action' })}`}</p>
        </div>
      </div>
      {filtersVisible && filtersList()}
    </div>
  );
};

export default DegreeFilters;

DegreeFilters.propTypes = {
  color: PropTypes.string.isRequired,
  degrees: PropTypes.array.isRequired,
  setDegreeFilter: PropTypes.func.isRequired,
  degreesShowing: PropTypes.number.isRequired,
};
